import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "../components/Alert";
import { API_URLS } from "../services/api";
import apiRequest from "../services/api";

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const cls = (...args) => args.filter(Boolean).join(" ");

// ─── icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  survey:     "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  activeSurvey:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  users:      "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  user:       "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  chat:       "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
  globe:      "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  plus:       "M12 4v16m8-8H4",
  addUser:    "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z",
  map:        "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  trending:   "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
  check:      "M5 13l4 4L19 7",
  clock:      "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  dot:        "M12 12m-2 0a2 2 0 104 0 2 2 0 10-4 0",
};

// ─── KPI colour palettes ───────────────────────────────────────────────────────
const PALETTES = [
  { bg: "bg-blue-600",   light: "bg-blue-50",   text: "text-blue-600",   ring: "ring-blue-200"   },
  { bg: "bg-emerald-600",light: "bg-emerald-50", text: "text-emerald-600",ring: "ring-emerald-200" },
  { bg: "bg-violet-600", light: "bg-violet-50",  text: "text-violet-600", ring: "ring-violet-200"  },
  { bg: "bg-indigo-600", light: "bg-indigo-50",  text: "text-indigo-600", ring: "ring-indigo-200"  },
  { bg: "bg-amber-500",  light: "bg-amber-50",   text: "text-amber-600",  ring: "ring-amber-200"   },
  { bg: "bg-teal-600",   light: "bg-teal-50",    text: "text-teal-600",   ring: "ring-teal-200"    },
];

// ─── Medal colours for leaderboard ────────────────────────────────────────────
const MEDAL = [
  "bg-amber-100 text-amber-700 ring-1 ring-amber-300",
  "bg-slate-100 text-slate-600 ring-1 ring-slate-300",
  "bg-orange-100 text-orange-700 ring-1 ring-orange-300",
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState("");
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const resp = await apiRequest(`${API_URLS.admin.dashboard}?_t=${timestamp}`);
      setDashboardData(resp.data);
    } catch (err) { setError(err.message || "Failed to load dashboard"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-72 gap-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      </div>
      <p className="text-sm text-slate-400 tracking-wide font-medium">Loading dashboard…</p>
    </div>
  );

  const overview = dashboardData?.overview || {};

  // ── KPI cards data ───────────────────────────────────────────────────────────
  const kpiCards = [
    { label: "Total Surveys",        value: overview.total_surveys,        icon: ICONS.survey,      pal: PALETTES[0] },
    { label: "Active Surveys",       value: overview.active_surveys,       icon: ICONS.activeSurvey,pal: PALETTES[1] },
    { label: "Total Enumerators",    value: overview.total_enumerators,    icon: ICONS.users,       pal: PALETTES[2] },
    { label: "Active Enumerators",   value: overview.active_enumerators,   icon: ICONS.user,        sub: "Last 30 days", pal: PALETTES[3] },
    { label: "Total Responses",      value: overview.total_responses,      icon: ICONS.chat,        sub: "Last 30 days", pal: PALETTES[4] },
    { label: "Regions",              value: overview.total_regions,        icon: ICONS.globe,       pal: PALETTES[5] },
  ];

  // ── derived bar-chart data ───────────────────────────────────────────────────
  const trends    = dashboardData?.response_trends || [];
  const maxTrend  = Math.max(...trends.map(t => t.responses), 1);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Page header ───────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Survey administration overview
              {dashboardData?.date_range && (
                <span className="ml-2 text-slate-300">·  {dashboardData.date_range.start_date} — {dashboardData.date_range.end_date}</span>
              )}
            </p>
          </div>
          {/* Quick-action pills */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: "New Survey",      icon: ICONS.plus,    to: "/surveys",     cls: "bg-blue-600 hover:bg-blue-700 text-white" },
              { label: "Add Enumerator",  icon: ICONS.addUser, to: "/enumerators", cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
              { label: "Manage Regions",  icon: ICONS.map,     to: "/regions",     cls: "bg-violet-600 hover:bg-violet-700 text-white" },
            ].map(btn => (
              <button
                key={btn.label}
                onClick={() => navigate(btn.to)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${btn.cls}`}
              >
                <Icon d={btn.icon} className="w-4 h-4" />
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── KPI cards ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {kpiCards.map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
            >
              <div className={`w-9 h-9 ${c.pal.bg} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={c.icon} className="w-4.5 h-4.5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 leading-none">
                  {c.value ?? "—"}
                </p>
                <p className="text-xs text-slate-500 mt-1 font-medium leading-tight">{c.label}</p>
                {c.sub && <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* ── Row 2: Trends + Top Performers ───────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* Response Trends — takes 3 cols */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Response Trends</h2>
                <p className="text-xs text-slate-400 mt-0.5">Last 7 days</p>
              </div>
              <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full font-semibold border border-blue-100">
                {trends.reduce((s, t) => s + (t.responses || 0), 0)} total
              </span>
            </div>
            {trends.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No trend data available</p>
            ) : (
              <div className="space-y-3">
                {trends.map((t, i) => {
                  const w = maxTrend > 0 ? (t.responses / maxTrend) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-10 text-xs font-medium text-slate-500 shrink-0 text-right">{t.day}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-end pr-2 transition-all duration-500"
                          style={{ width: `${Math.max(w, t.responses > 0 ? 8 : 0)}%` }}
                        >
                          {t.responses > 0 && (
                            <span className="text-xs font-bold text-white">{t.responses}</span>
                          )}
                        </div>
                        {t.responses === 0 && (
                          <span className="absolute inset-y-0 left-3 flex items-center text-xs text-slate-400">0</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Performers — takes 2 cols */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Top Performers</h2>
                <p className="text-xs text-slate-400 mt-0.5">By responses collected</p>
              </div>
              <Icon d={ICONS.trending} className="w-4 h-4 text-emerald-500" />
            </div>
            {!dashboardData?.top_enumerators?.length ? (
              <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
            ) : (
              <div className="space-y-2.5">
                {dashboardData.top_enumerators.map((en, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                    <span className={cls(
                      "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                      i < 3 ? MEDAL[i] : "bg-slate-100 text-slate-500"
                    )}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{en.name}</p>
                      <p className="text-xs text-slate-400 truncate">{en.region}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-slate-900">{en.responses_count}</p>
                      <p className="text-xs text-slate-400">responses</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 3: Survey Performance + Regional Performance ──────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Survey Performance */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Survey Performance</h2>
                <p className="text-xs text-slate-400 mt-0.5">Top 5 surveys</p>
              </div>
              <Icon d={ICONS.activeSurvey} className="w-4 h-4 text-slate-400" />
            </div>
            {!dashboardData?.survey_performance?.length ? (
              <p className="text-sm text-slate-400 p-6 text-center">No surveys found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {["Survey", "Questions", "Responses", "Completion", "Status"].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboardData.survey_performance.map((s) => {
                      const rate = s.completion_rate ?? 0;
                      const rateColor = rate >= 80 ? "text-emerald-600" : rate >= 50 ? "text-amber-600" : "text-red-500";
                      return (
                        <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800 truncate max-w-[180px]" title={s.title}>{s.title}</p>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-600">{s.questions_count}</td>
                          <td className="px-4 py-3 text-center font-semibold text-slate-800">{s.responses_count}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-semibold ${rateColor}`}>{rate}%</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cls(
                              "px-2 py-0.5 rounded-full text-xs font-semibold",
                              s.is_active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"
                            )}>
                              {s.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Regional Performance */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Regional Performance</h2>
                <p className="text-xs text-slate-400 mt-0.5">Responses by region</p>
              </div>
              <Icon d={ICONS.globe} className="w-4 h-4 text-slate-400" />
            </div>
            {!dashboardData?.regional_performance?.length ? (
              <p className="text-sm text-slate-400 p-6 text-center">No regional data available</p>
            ) : (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dashboardData.regional_performance.map((region, ri) => {
                  const pal = PALETTES[ri % PALETTES.length];
                  return (
                    <div key={region.id} className={cls("rounded-xl border p-4 hover:shadow-sm transition-shadow", "border-slate-200 bg-slate-50/50")}>
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-8 h-8 ${pal.bg} rounded-lg flex items-center justify-center shrink-0`}>
                          <Icon d={ICONS.globe} className="w-4 h-4 text-white" />
                        </div>
                        <p className="font-semibold text-slate-800 text-sm leading-tight pt-0.5">{region.name}</p>
                      </div>
                      <div className="space-y-1.5">
                        {[
                          { label: "Enumerators",          val: region.enumerators_count },
                          { label: "Responses",             val: region.responses_count },
                          { label: "Avg / Enumerator",      val: region.avg_responses_per_enumerator },
                        ].map(({ label, val }) => (
                          <div key={label} className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">{label}</span>
                            <span className={cls("font-bold", pal.text)}>{val ?? "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Recent Activity ───────────────────────────────────────────────── */}
        {dashboardData?.recent_activity?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Recent Activity</h2>
                <p className="text-xs text-slate-400 mt-0.5">Latest survey submissions</p>
              </div>
              <span className="text-xs px-2.5 py-1 bg-slate-100 text-slate-500 rounded-full font-semibold border border-slate-200">
                {dashboardData.recent_activity.length} entries
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {dashboardData.recent_activity.map((a, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-3.5 hover:bg-slate-50/60 transition-colors">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center pt-1 shrink-0">
                    <div className="w-2 h-2 rounded-full bg-blue-500 ring-4 ring-blue-50" />
                    {i < dashboardData.recent_activity.length - 1 && (
                      <div className="w-px flex-1 bg-slate-200 mt-1.5 min-h-[20px]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pb-1">
                    <p className="text-sm text-slate-800 leading-snug">
                      <span className="font-semibold">{a.enumerator}</span>
                      <span className="text-slate-500"> surveyed </span>
                      <span className="font-semibold">{a.outlet_name}</span>
                      <span className="text-slate-500"> in </span>
                      <span className="text-slate-700">{a.area}</span>
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-slate-400">{a.survey}</span>
                      <span className="text-slate-200">·</span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Icon d={ICONS.clock} className="w-3 h-3" />
                        {a.created_at}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}